import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all districts
export const fetchDistricts = createAsyncThunk(
  "districts/fetchDistricts",
  async ({ page = 1, per_page = 15, status, search, state_uuid } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    if (state_uuid) params.append("state_uuid", state_uuid);
    
    const res = await http.get(`/dashboard/districts?${params.toString()}`);
    return res;
  }
);

// Create district
export const createDistrict = createAsyncThunk(
  "districts/createDistrict",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/districts", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create district"
      );
    }
  }
);

// Fetch district by UUID
export const fetchDistrictById = createAsyncThunk(
  "districts/fetchDistrictById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/districts/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch district details"
      );
    }
  }
);

// Update district
export const updateDistrict = createAsyncThunk(
  "districts/updateDistrict",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/dashboard/districts/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update district"
      );
    }
  }
);

// Delete district
export const deleteDistrict = createAsyncThunk(
  "districts/deleteDistrict",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/districts/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete district"
      );
    }
  }
);

// Toggle district status
export const toggleDistrictStatus = createAsyncThunk(
  "districts/toggleDistrictStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/districts/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle district status"
      );
    }
  }
);

const districtsSlice = createSlice({
  name: "districts",
  initialState: {
    districts: [],
    pagination: null,
    district: null,
    districtCreateStatus: "idle",
    districtUpdateStatus: "idle",
    districtDeleteStatus: "idle",
    districtFetchStatus: "idle",
    districtStatusChangeStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetDistrictStatuses: (state) => {
      state.districtCreateStatus = "idle";
      state.districtUpdateStatus = "idle";
      state.districtDeleteStatus = "idle";
      state.districtStatusChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch districts
      .addCase(fetchDistricts.fulfilled, (state, action) => {
        state.districts = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create district
      .addCase(createDistrict.pending, (state) => {
        state.districtCreateStatus = "loading";
      })
      .addCase(createDistrict.fulfilled, (state, action) => {
        state.districtCreateStatus = "success";
        state.district = action.payload.data;
      })
      .addCase(createDistrict.rejected, (state) => {
        state.districtCreateStatus = "failed";
      })

      // Fetch district by ID
      .addCase(fetchDistrictById.pending, (state) => {
        state.districtFetchStatus = "loading";
      })
      .addCase(fetchDistrictById.fulfilled, (state, action) => {
        state.districtFetchStatus = "success";
        state.district = action.payload.data;
      })
      .addCase(fetchDistrictById.rejected, (state) => {
        state.districtFetchStatus = "failed";
        state.district = null;
      })

      // Update district
      .addCase(updateDistrict.pending, (state) => {
        state.districtUpdateStatus = "loading";
      })
      .addCase(updateDistrict.fulfilled, (state, action) => {
        state.districtUpdateStatus = "success";
        const index = state.districts.findIndex(
          (dist) => dist.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.districts[index] = action.payload.data;
        }
        state.district = action.payload.data;
      })
      .addCase(updateDistrict.rejected, (state) => {
        state.districtUpdateStatus = "failed";
      })

      // Delete district
      .addCase(deleteDistrict.pending, (state) => {
        state.districtDeleteStatus = "loading";
      })
      .addCase(deleteDistrict.fulfilled, (state, action) => {
        state.districtDeleteStatus = "success";
        state.districts = state.districts.filter(
          (dist) => dist.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteDistrict.rejected, (state) => {
        state.districtDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleDistrictStatus.pending, (state) => {
        state.districtStatusChangeStatus = "loading";
      })
      .addCase(toggleDistrictStatus.fulfilled, (state, action) => {
        state.districtStatusChangeStatus = "success";
        const index = state.districts.findIndex(
          (dist) => dist.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.districts[index] = action.payload.data;
        }
      })
      .addCase(toggleDistrictStatus.rejected, (state) => {
        state.districtStatusChangeStatus = "failed";
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetDistrictStatuses } = districtsSlice.actions;
export default districtsSlice.reducer;
